import { useState, useRef, useEffect } from "react";
import "./AudioPlayer.css";

export default function AudioPlayer({ src, isMine }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const audioRef = useRef(null);
  const progressRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleProgressClick = (e) => {
    const audio = audioRef.current;
    const progress = progressRef.current;
    if (!audio || !progress) return;

    const rect = progress.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    audio.currentTime = percent * duration;
  };

  const togglePlaybackRate = () => {
    const rates = [1, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextRate = rates[(currentIndex + 1) % rates.length];
    setPlaybackRate(nextRate);
    if (audioRef.current) {
      audioRef.current.playbackRate = nextRate;
    }
  };

  const formatTime = (time) => {
    if (!time || isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const generateWaveform = () => {
    const bars = [];
    const numBars = 35;
    for (let i = 0; i < numBars; i++) {
      const height = 20 + Math.sin(i * 0.5) * 15 + Math.cos(i * 0.3) * 10;
      const isPlayed = (i / numBars) * 100 <= progress;
      bars.push(
        <div
          key={i}
          className={`waveform_bar ${isPlayed ? "played" : ""}`}
          style={{ height: `${Math.max(8, Math.min(height, 40))}px` }}
        />
      );
    }
    return bars;
  };

  return (
    <div className={`audio_player ${isMine ? "mine" : "theirs"}`}>
      <audio ref={audioRef} src={src} preload="metadata" />

      <button className="play_btn" onClick={togglePlay}>
        {isPlaying ? (
          <svg viewBox="0 0 24 24" width="28" height="28">
            <path fill="currentColor" d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" width="28" height="28">
            <path fill="currentColor" d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>

      <div className="waveform_container" ref={progressRef} onClick={handleProgressClick}>
        <div className="waveform">{generateWaveform()}</div>
      </div>

      <div className="audio_info">
        <span className="audio_time">
          {isPlaying ? formatTime(currentTime) : formatTime(duration)}
        </span>
        {isPlaying && (
          <button className="speed_btn" onClick={togglePlaybackRate}>
            {playbackRate}x
          </button>
        )}
      </div>

      <div className="mic_icon">
        <svg viewBox="0 0 12 20" width="12" height="20">
          <path
            fill="#8696a0"
            d="M6 11.745a2 2 0 0 0 2-2V4a2 2 0 0 0-4 0v5.745a2 2 0 0 0 2 2zm3.495-2.12c0 1.927-1.568 3.495-3.495 3.495s-3.495-1.568-3.495-3.495H1.5c0 2.307 1.725 4.212 3.953 4.493v2.382h1.094v-2.382c2.228-.281 3.953-2.186 3.953-4.493H9.495z"
          />
        </svg>
      </div>
    </div>
  );
}
